import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, Trash2 } from 'lucide-react'
import { ApiService } from '@/services/api'
import type { OrdoreMiniLicense } from '@/services/api'
import { toast } from 'sonner'

interface DeleteOrdoreMiniLicenseModalProps {
  license: OrdoreMiniLicense | null
  open: boolean
  onClose: () => void
  onSuccess: (id: string) => void
}

export default function DeleteOrdoreMiniLicenseModal({ license, open, onClose, onSuccess }: DeleteOrdoreMiniLicenseModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!license) return null

  const handleDelete = async () => {
    setError(null)
    setIsLoading(true)
    try {
      const response = await ApiService.deleteOrdoreMiniLicense(license._id)
      if (response.success) {
        toast.success(`Deleted license for ${license.restaurantName}`)
        onSuccess(license._id)
        onClose()
      } else {
        setError(response.message || 'Failed to delete license')
        toast.error(response.message || 'Failed to delete license')
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to delete license'
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !isLoading) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            Delete License
          </DialogTitle>
          <DialogDescription>
            This will permanently remove <span className="font-medium">{license.restaurantName}</span>'s Ordore Mini license
            ({license.licenseKey}) from the system. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete License
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
